import { useRef, useState } from 'react';
import type { UploadFile } from 'antd/es/upload/interface';
import { Button, Input, message, Typography } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import DocumentUploader from '../../../components/Upload/DocumentUploader';
import styles from './PolicyUpload.module.scss';
import {
  POLICY_ACCEPT_STRING,
  POLICY_ALLOWED_FORMAT_LABEL,
  isPolicyFileAllowed,
} from '../utils/fileValidation';
const { Paragraph } = Typography;

export type PolicyUploadPayload = {
  location: string;
  files: File[];
};

interface PolicyUploadProps {
  onSubmit?: (payload: PolicyUploadPayload) => Promise<void> | void;
}

const PolicyUpload = ({ onSubmit }: PolicyUploadProps) => {
  const [location, setLocation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const inFlight = useRef(false);

  const handleUpload = async () => {
    if (inFlight.current) return;
    const trimmedLocation = location.trim();
    if (!trimmedLocation) {
      message.warning('Enter a location before uploading.');
      return;
    }

    if (selectedFiles.length === 0) {
      message.warning('Select a document before uploading.');
      return;
    }

    try {
      inFlight.current = true;
      setSubmitting(true);
      await onSubmit?.({ location: trimmedLocation, files: selectedFiles });
      setLocation('');
      setSelectedFiles([]);
      setFileList([]);
    } catch (error) {
      message.error('Failed to upload policy document.');
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const handleFilesChange = (files: File[], nextFileList: UploadFile[]) => {
    const validFiles = files.filter((file) => isPolicyFileAllowed(file.name));
    const filteredFileList = nextFileList.filter((item) => isPolicyFileAllowed(item.name));

    if (files.length > 0 && validFiles.length !== files.length) {
      message.error(`Unsupported file type. Allowed formats: ${POLICY_ALLOWED_FORMAT_LABEL}.`);
    }

    setSelectedFiles(validFiles);
    setFileList(filteredFileList);
  };

  return (
    <div className={styles.container}>
      <Input
        value={location}
        onChange={(event) => setLocation(event.target.value)}
        placeholder="Enter policy location"
        className={styles.input}
        allowClear
      />
      <Paragraph className="!text-[#9ca3af]">Supported formats: {POLICY_ALLOWED_FORMAT_LABEL}</Paragraph>
      <div className={styles.uploader}>
        <DocumentUploader
          className={`${styles.dragger} h-full`}
          fileList={fileList}
          accept={POLICY_ACCEPT_STRING}
          onFilesChange={handleFilesChange}
          multiple
          disabled={submitting}
        />
      </div>
      <div className={styles.actions}>
        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={handleUpload}
          loading={submitting}
          disabled={submitting}
          className={styles.uploadButton}
        >
          Upload
        </Button>
      </div>
    </div>
  );
};

export default PolicyUpload;
